import { useExpFeatures } from '@/hooks';
import { Tooltip } from '@/ui';
import { BugReportIcon, CloseIcon, MinimizeIcon } from '@/ui/icons';
import { useCallback, useEffect, useState } from 'react';
import FeedbackForm from './FeedbackForm';

// Floating action button that hosts the shared FeedbackForm (variant="panel").
// The FAB is an experimental surface: it renders only when the user opts into
// Funciones experimentales (useExpFeatures), the same gate the Sidebar's "Próx"
// items use. The form itself is reused, ungated, by the Ayuda "Reportar un
// problema" modal (permanent support, always available).

const iconButtonStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 4,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'inherit',
};

export default function FeedbackWidget() {
  const [exp] = useExpFeatures();
  const [mode, setMode] = useState('closed'); // 'closed' | 'open' | 'minimized'
  const [draft, setDraft] = useState(null);

  // Publish how much bottom-right space this widget occupies so overlays
  // anchored to that corner (the toast stack) can clear it.
  useEffect(() => {
    if (!exp) return undefined;
    const root = document.documentElement;
    root.style.setProperty('--corner-clearance', '76px');
    return () => root.style.removeProperty('--corner-clearance');
  }, [exp]);

  const open = useCallback(() => setMode('open'), []);
  const minimize = useCallback(() => setMode('minimized'), []);
  const fullClose = useCallback(() => {
    setMode('closed');
    setDraft(null);
  }, []);

  // All hooks run above this guard, so the hook count stays stable when the
  // experimental flag flips.
  if (!exp) return null;

  // ── Closed: floating action button only ───────────────────────────────────
  if (mode === 'closed') {
    return (
      <Tooltip tip="Reportar bug / sugerencia" placement="left">
        <button
          type="button"
          onClick={open}
          aria-label="Reportar bug / sugerencia"
          title="Reportar bug / sugerencia"
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1200,
            width: 40,
            height: 40,
            borderRadius: '50%',
            border: 'none',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--blue)',
            color: '#fff',
            boxShadow: '0 3px 8px rgba(0,0,0,0.25)',
          }}
        >
          <BugReportIcon size={18} />
        </button>
      </Tooltip>
    );
  }

  const draftPreview = draft ?? 'Reporte en curso';

  // Open + minimized share one mounted FeedbackForm so the draft survives
  // minimize. Minimized just hides the panel and shows the chip on top.
  return (
    <>
      <div
        role="dialog"
        aria-label="Reportar bug / sugerencia"
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 'var(--z-feedback)',
          width: 'min(420px, calc(100vw - 32px))',
          maxHeight: '80vh',
          display: mode === 'open' ? 'flex' : 'none',
          flexDirection: 'column',
          borderRadius: 0,
          overflow: 'hidden',
          backgroundColor: 'var(--paper-elev)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.28)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 16px',
            borderBottom: '1px solid var(--rule)',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BugReportIcon size={18} style={{ color: 'var(--blue)' }} />
            <h2
              style={{
                margin: 0,
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--ink)',
              }}
            >
              Reportar bug / sugerencia
            </h2>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <Tooltip tip="Minimizar">
              <button
                type="button"
                style={iconButtonStyle}
                onClick={minimize}
                aria-label="Minimizar"
              >
                <MinimizeIcon size={18} />
              </button>
            </Tooltip>
            <Tooltip tip="Cerrar">
              <button
                type="button"
                style={iconButtonStyle}
                onClick={fullClose}
                aria-label="Cerrar"
              >
                <CloseIcon size={18} />
              </button>
            </Tooltip>
          </div>
        </div>

        <FeedbackForm
          variant="panel"
          onClose={fullClose}
          onDraftChange={setDraft}
        />
      </div>

      {mode === 'minimized' && (
        <button
          type="button"
          onClick={open}
          aria-label="Restaurar reporte"
          title={`Reportando: ${draftPreview}`}
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1200,
            maxWidth: 280,
            cursor: 'pointer',
            boxShadow: '0 3px 8px rgba(0,0,0,0.25)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            height: 32,
            padding: '0 12px',
            borderRadius: 'var(--r-pill)',
            border: 'none',
            backgroundColor: 'var(--blue)',
            color: '#fff',
            fontSize: '0.82rem',
          }}
        >
          <BugReportIcon size={18} />
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {`Reportando: ${draftPreview}`}
          </span>
        </button>
      )}
    </>
  );
}
